// @flow

import * as React from 'react';
import Icon, { type IconName } from '@ta/core-ui.icon';
import classNames from '@ta/common.classnames';
import styles from './OverflowUIListItem.css';

type Props = {|
  /** child, if any, is a function that accepts the `close` callback as the first argument */
  children?: React.Node,
  /** optional icon name to display in-line with the list item. see styleguide for a list of icon names */
  icon?: IconName,
  /** `close` prop is passed in by OverflowUIList when this OverflowUIListItem is a child of said OverflowUIList. Calling `close` will close the OverflowUI popover */
  close: () => void,
  onClick: ({| event: SyntheticEvent<HTMLOListElement>, close: () => void |}) => void,
  /**  whether the element should be active (default) or inactive (greyed out, non-clickable) */
  active?: boolean,
|};

/**
 * OverflowUIListItem is a component to be used as a child to OverflowUIList. This component represents a single
 * menu entry in the OverflowUIList. See OverflowUI.story.jsx for more info
 */
const OverflowUIListItem = ({ children, icon, close, onClick, active }: Props) => (
  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
  <li
    className={classNames(styles.item, { [styles.inactive]: !active })}
    onClick={event => {
      if (active) {
        event.preventDefault();
        onClick({ event, close });
      }
    }}
  >
    {icon && (
      <span className={styles.icon}>
        <Icon name={icon} />
      </span>
    )}
    {children}
  </li>
);

OverflowUIListItem.defaultProps = {
  children: undefined,
  icon: undefined,
  close: () => {},
  active: true,
};

export default OverflowUIListItem;
